// Bake a top-down terrain image once per world. The radar drives this image
// every frame with a single transformed drawImage call — no per-frame noise
// sampling, no per-frame pixel work.

import { TerrainSampler } from "./Noise";
import { TERRAIN, type MapParams } from "../shared/protocol";
import { type Building } from "../shared/Buildings";

interface RGB { r: number; g: number; b: number; }
const PAL = {
  seaDeep:   { r: 13,  g: 55,  b: 86  },
  sea:       { r: 42,  g: 111, b: 151 },
  beach:     { r: 222, g: 193, b: 139 },
  grass:     { r: 79,  g: 138, b: 63  },
  grassDark: { r: 61,  g: 111, b: 48  },
  rock:      { r: 125, g: 124, b: 121 },
  snow:      { r: 244, g: 244, b: 248 },
  asphalt:   { r: 58,  g: 63,  b: 72  },
  asphaltLt: { r: 82,  g: 88,  b: 95  },
  building:  { r: 168, g: 175, b: 184 },
  road:      { r: 36,  g: 40,  b: 48  },
};

function lerpRGB(a: RGB, b: RGB, t: number): RGB {
  return { r: a.r + (b.r - a.r) * t, g: a.g + (b.g - a.g) * t, b: a.b + (b.b - a.b) * t };
}

function colorForHeight(h: number, sea: number, maxH: number, urban: boolean): RGB {
  if (urban) {
    // Mostly gray asphalt
    if (h < sea) return PAL.road;
    return PAL.asphalt;
  }
  if (h < sea - 1)      return PAL.seaDeep;
  if (h < sea)          return lerpRGB(PAL.seaDeep, PAL.sea, h - (sea - 1));
  if (h < sea + 0.4)    return PAL.beach;
  if (h < sea + 4)      return lerpRGB(PAL.beach, PAL.grass, (h - sea - 0.4) / 3.6);
  if (h < maxH * 0.45)  return lerpRGB(PAL.grass, PAL.grassDark, (h - 4 - sea) / Math.max(1, (maxH * 0.45 - 4 - sea)));
  if (h < maxH * 0.72)  return lerpRGB(PAL.grassDark, PAL.rock, (h - maxH * 0.45) / (maxH * 0.27));
  return lerpRGB(PAL.rock, PAL.snow, Math.min(1, (h - maxH * 0.72) / (maxH * 0.28)));
}

function shaped(sampler: TerrainSampler, x: number, z: number, params: MapParams): number {
  const raw = sampler.height(x, z, TERRAIN.maxHeight);
  let h = raw * params.heightAmp;
  if (params.flatten > 0) h = h * (1 - params.flatten) + params.seaLevel * params.flatten;
  return h;
}

export function bakeMinimap(
  sampler: TerrainSampler,
  resolution = 384,
  params: MapParams,
  buildings: Building[],
): HTMLCanvasElement {
  const cvs = document.createElement("canvas");
  cvs.width = resolution; cvs.height = resolution;
  const ctx = cvs.getContext("2d", { alpha: false })!;
  const img = ctx.createImageData(resolution, resolution);
  const size = TERRAIN.size;
  const sea = params.seaLevel;
  const maxH = TERRAIN.maxHeight;
  const step = size / resolution;
  const urban = params.collidableBuildings;

  for (let py = 0; py < resolution; py++) {
    const wz = (py / resolution - 0.5) * size;
    for (let px = 0; px < resolution; px++) {
      const wx = (px / resolution - 0.5) * size;
      const h  = shaped(sampler, wx, wz, params);
      const c  = colorForHeight(h, sea, maxH, urban);

      let shade = 1.0;
      if (h > sea + 0.5) {
        const hN = shaped(sampler, wx + step, wz, params);
        const hE = shaped(sampler, wx, wz + step, params);
        const dx = (hN - h) / step;
        const dz = (hE - h) / step;
        const slope = -dx - dz;
        shade = 1 + slope * 0.18;
        if (shade < 0.7) shade = 0.7;
        if (shade > 1.25) shade = 1.25;
      }

      const idx = (py * resolution + px) * 4;
      img.data[idx + 0] = Math.min(255, c.r * shade);
      img.data[idx + 1] = Math.min(255, c.g * shade);
      img.data[idx + 2] = Math.min(255, c.b * shade);
      img.data[idx + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);

  // Overlay building footprints (especially important for metropolis).
  if (buildings.length) {
    const pxPerWorld = resolution / size;
    ctx.save();
    ctx.translate(resolution / 2, resolution / 2);
    for (const b of buildings) {
      const x = b.x * pxPerWorld;
      const z = b.z * pxPerWorld;
      const w = Math.max(1, b.w * pxPerWorld);
      const d = Math.max(1, b.d * pxPerWorld);
      ctx.fillStyle = b.kind === 1
        ? `rgba(${PAL.building.r}, ${PAL.building.g}, ${PAL.building.b}, 1)`
        : `rgba(${PAL.building.r * 0.7}, ${PAL.building.g * 0.6}, ${PAL.building.b * 0.5}, 1)`;
      ctx.fillRect(x - w / 2, z - d / 2, w, d);
    }
    ctx.restore();
  }

  return cvs;
}
